import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

// Redux
import { connect } from 'react-redux';
import { setAlert } from '../../redux/actions/alertActions';

// Server
import { getNetwork, downloadNetworkCsv } from '../../api';

// Components
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import { PillTrack, Pill } from '../../components/Pill';

// Constants
import NetworkNode from '../../lib/NetworkNode';
import { UserTypes } from '../../constants';
import { T, FONT_MONO, INDICATOR, INDICATOR_TEXT, ROW_HEIGHT } from '../../constants/tokens';
import {
  statusOf, isWeak, qualityText, qualityFill, QUALITY_LEGEND, WEAK_QUALITY,
  formatQuality, formatRssi, formatLatency, latencyText, formatBattery, batteryText, formatSeen, describe
} from './format';

/**
 * How often the screen re-reads what the server has recorded. The server never
 * asks the network for a node list (GetNode must not be used); every device
 * reports its own diagnostics about every 10 minutes.
 */
const RELOAD_MS = 30 * 1000;

/** The longest route drawn before giving up on a loop in the next-hop data. */
const MAX_CHAIN = 8;

const TABLE_TABS = [
  { key: 'All', label: 'All nodes', kinds: null },
  { key: 'GW', label: 'Gateways', kinds: [NetworkNode.KINDS.GATEWAY] },
  { key: 'Anchors', label: 'Anchors', kinds: [NetworkNode.KINDS.ANCHOR] },
  { key: 'Hubs', label: 'Hubs', kinds: [NetworkNode.KINDS.HUB] },
  { key: 'Badges', label: 'Badges', kinds: [NetworkNode.KINDS.BADGE] }
];

const TABLE_GRID = 'minmax(170px,1.4fr) 70px 130px 90px 110px 130px 90px 120px minmax(120px,1fr) 56px';
const TABLE_COLUMNS = ['Node', 'Role', 'Device status', 'Latency', 'Last seen', 'Battery', 'Signal', 'Signal quality', 'Next hop', 'Hops'];

const mono = { fontFamily: FONT_MONO };
const eyebrow = {
  fontFamily: FONT_MONO,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  textTransform: 'uppercase',
  color: T.body
};
const sectionTitle = {
  margin: 0,
  fontSize: 32,
  fontWeight: 600,
  lineHeight: '40px',
  letterSpacing: '-1.28px',
  color: T.ink
};
const panel = {
  background: T.elevated,
  border: '1px solid ' + T.hairline,
  borderRadius: 8,
  padding: 24
};

const useStyles = makeStyles({
  rail: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 24,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    padding: 2,
    '&::-webkit-scrollbar': { display: 'none' }
  },
  gateway: {
    flex: '1 0 300px',
    minWidth: 0,
    textAlign: 'left',
    fontFamily: 'inherit',
    background: T.canvas,
    borderRadius: 6,
    padding: 24,
    cursor: 'pointer',
    transition: 'background 120ms ease',
    '&:hover': { background: T.surfaceSoft },
    '&:focus-visible': { outline: '2px solid ' + T.ink, outlineOffset: 2 }
  },
  meshNode: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'inherit',
    padding: '10px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    minWidth: 0,
    '&:hover': { borderColor: T.ink },
    '&:focus-visible': { outline: '2px solid ' + T.ink, outlineOffset: 2 }
  },
  row: {
    display: 'grid',
    gridTemplateColumns: TABLE_GRID,
    gap: 16,
    alignItems: 'center',
    padding: '0 24px',
    borderBottom: '1px solid ' + T.hairlineSoft,
    minHeight: ROW_HEIGHT.dense,
    cursor: 'pointer',
    '&:hover': { background: T.surfaceSoft }
  },
  button: {
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    height: 40,
    padding: '0 20px',
    borderRadius: 6,
    border: '1px solid ' + T.hairline,
    background: T.elevated,
    color: T.ink,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover:not(:disabled)': { background: T.surfaceSoft },
    '&:disabled': { color: T.mutedSoft, cursor: 'default' },
    '&:focus-visible': { outline: '2px solid ' + T.ink, outlineOffset: 2 }
  }
});

const Dot = ({ color, size = 8 }) => (
  <span style={{ width: size, height: size, borderRadius: 9999, background: color, flex: 'none', display: 'inline-block' }} />
);

Dot.propTypes = { color: PropTypes.string, size: PropTypes.number };

/**
 * Walk a node's next hops up to its gateway.
 *
 * Stops at a gateway, at an address the network list does not contain (shown
 * as an unknown hop, since that is itself worth seeing), or after MAX_CHAIN
 * hops, which only happens when the next-hop data loops.
 */
function routeOf(start, byAddress) {
  const chain = [];
  const visited = new Set();
  let current = start;
  while (current && chain.length < MAX_CHAIN && !visited.has(current.getNodeAddress())) {
    chain.push(current);
    visited.add(current.getNodeAddress());
    if (current.isGateway()) break;
    const next = current.getNextHop();
    if (next == null) break;
    current = byAddress.get(next) ?? NetworkNode.thaw({ nodeAddress: next, kind: NetworkNode.KINDS.UNKNOWN });
  }
  return chain;
}

function NetworkPerformance({ userType, setError }) {
  const classes = useStyles();
  const [network, setNetwork] = useState(null);
  const [focusAddress, setFocusAddress] = useState(null);
  const [tab, setTab] = useState('All');
  const [busy, setBusy] = useState(null);
  const mounted = useRef(true);

  const isSuper = userType === UserTypes.SUPER;

  const load = useCallback(() => {
    return getNetwork()
      .then((result) => {
        if (mounted.current) setNetwork(result);
      })
      .catch((error) => setError(error ? error : 'Could not retrieve the network.'));
  }, [setError]);

  useEffect(() => {
    mounted.current = true;
    if (!isSuper) return undefined;
    load();
    const timer = setInterval(load, RELOAD_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [isSuper, load]);

  const nodes = useMemo(() => network?.nodes ?? [], [network]);
  const byAddress = useMemo(() => new Map(nodes.map((n) => [n.getNodeAddress(), n])), [nodes]);
  const gateways = nodes.filter((n) => n.isGateway());
  const members = nodes.filter((n) => !n.isGateway());
  const anchors = nodes.filter((n) => n.getKind() === NetworkNode.KINDS.ANCHOR);
  const weak = members.filter(isWeak);

  const focus = byAddress.get(focusAddress) ?? weak[0] ?? members[0] ?? null;
  const route = focus ? routeOf(focus, byAddress) : [];

  // Hop tiers: gateways at the top, then every other node by its hop count.
  // Nodes that have not reported a hop count go in a final tier of their own.
  const tiers = useMemo(() => {
    const byHops = new Map();
    members.forEach((n) => {
      const hops = n.getHopCount() ?? Infinity;
      if (!byHops.has(hops)) byHops.set(hops, []);
      byHops.get(hops).push(n);
    });
    return [{ key: 'gw', nodes: gateways }].concat(
      [...byHops.keys()].sort((a, b) => a - b).map((hops) => ({ key: String(hops), nodes: byHops.get(hops) }))
    );
  }, [members, gateways]);

  if (!isSuper) return <Redirect to='/dashboard/overview' />;

  const loading = network == null;
  const gatewaysOnline = gateways.filter((g) => g.getIsConnected() === true).length;
  const anchorsOnline = anchors.filter((a) => a.getIsConnected() === true).length;
  const anchorsOff = anchors.filter((a) => a.getIsConnected() === false).length;
  const latencies = members.map((n) => n.getLatencyMs()).filter((v) => v != null);
  const avgLatency = latencies.length ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length) : null;

  const tableTab = TABLE_TABS.find((t) => t.key === tab);
  const rows = nodes.filter((n) => tableTab.kinds == null || tableTab.kinds.includes(n.getKind()));

  const reload = () => {
    setBusy('reload');
    load().finally(() => mounted.current && setBusy(null));
  };

  const exportCsv = (scope) => {
    setBusy(scope);
    downloadNetworkCsv(scope)
      .catch((error) => setError(error ? error : 'Could not export the network.'))
      .finally(() => mounted.current && setBusy(null));
  };

  return (
    <>
      <PageHeader
        title='Network performance'
        subtitle={loading
          ? 'Loading…'
          : 'network id ' + (network.networkAddress ?? '—') + ' · ' + nodes.length + (nodes.length === 1 ? ' node' : ' nodes')}
        actions={
          <button type='button' className={classes.button} onClick={reload} disabled={busy != null}>
            {busy === 'reload' ? 'Reloading…' : 'Reload'}
          </button>
        }
      />

      {/* Stats */}
      <div className={classes.rail} style={{ marginBottom: 48 }} data-scroller>
        <StatCard
          style={{ flex: '1 0 236px' }}
          label='Gateways'
          value={gatewaysOnline + ' / ' + gateways.length}
          detail={gateways.length - gatewaysOnline > 0
            ? (gateways.length - gatewaysOnline) + ' unreachable'
            : (gateways.length ? 'All gateways reporting' : 'None on this network')}
          accent={gatewaysOnline < gateways.length ? INDICATOR_TEXT.error : INDICATOR_TEXT.success}
        />
        <StatCard
          style={{ flex: '1 0 236px' }}
          label='M2/M3 anchors'
          value={anchorsOnline + ' / ' + anchors.length}
          detail={anchors.length === 0 ? 'None on this network' : anchorsOff + ' off the mesh'}
          accent={anchorsOff > 0 ? INDICATOR_TEXT.warning : INDICATOR_TEXT.success}
        />
        <StatCard
          style={{ flex: '1 0 236px' }}
          label='Nodes on network'
          value={String(nodes.length)}
          detail='gateways, anchors, hubs and badges'
          accent={INDICATOR_TEXT.accent}
        />
        <StatCard
          style={{ flex: '1 0 236px' }}
          label='Weak links'
          value={weak.length + (weak.length === 1 ? ' node' : ' nodes')}
          detail={avgLatency == null ? 'no latency reported yet' : 'avg latency ' + avgLatency + ' ms'}
          accent={weak.length ? INDICATOR_TEXT.warning : INDICATOR_TEXT.success}
        />
      </div>

      {/* Gateways */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
        <h2 style={sectionTitle}>Gateways</h2>
        <span style={{ ...mono, fontSize: 12, color: T.body }}>network id {network?.networkAddress ?? '—'}</span>
      </div>
      <div className={classes.rail} style={{ marginBottom: 48 }} data-scroller>
        {!loading && gateways.length === 0 &&
          <div style={{ fontSize: 14, color: T.muted }}>No gateways reported on this network yet.</div>}
        {gateways.map((gw) => {
          const status = statusOf(gw);
          const mine = members.filter((n) => n.getSinkAddress() === gw.getNodeAddress());
          const count = (kind) => mine.filter((n) => n.getKind() === kind).length;
          return (
            <button
              type='button'
              key={gw.getNodeAddress()}
              className={classes.gateway}
              style={{ border: '1px solid ' + (gw.getIsConnected() === false ? INDICATOR.error : T.hairline) }}
              onClick={() => setFocusAddress(mine[0]?.getNodeAddress() ?? null)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...mono, fontSize: 16, fontWeight: 500, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {gw.getNodeAddress()}
                  </div>
                  <div style={{ ...eyebrow, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {gw.getLabel() ?? 'Gateway'}
                  </div>
                </div>
                <span style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, background: T.hairlineSoft, borderRadius: 9999, padding: '4px 12px', fontSize: 12, fontWeight: 500, lineHeight: '16px', color: T.ink }}>
                  <Dot color={status.dot} size={7} />{status.label}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ ...mono, fontSize: 28, fontWeight: 500, lineHeight: 1.2, color: T.ink }}>{mine.length}</span>
                <span style={{ ...mono, fontSize: 14, color: T.body }}>nodes connected</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16, paddingTop: 16, borderTop: '1px solid ' + T.hairlineSoft }}>
                {[['anchors', NetworkNode.KINDS.ANCHOR], ['hubs', NetworkNode.KINDS.HUB], ['badges', NetworkNode.KINDS.BADGE]].map(([k, kind]) => (
                  <div key={k} style={{ flex: '1 1 72px', minWidth: 0 }}>
                    <div style={{ ...mono, fontSize: 16, fontWeight: 500, color: T.ink }}>{count(kind)}</div>
                    <div style={eyebrow}>{k}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 14, lineHeight: '20px', color: T.body, marginTop: 16 }}>
                {[
                  gw.getAppVersion() && 'fw v' + gw.getAppVersion(),
                  'seen ' + formatSeen(gw)
                ].filter(Boolean).join(' · ')}
              </div>
            </button>
          );
        })}
      </div>

      {/* Mesh topology */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
        <h2 style={sectionTitle}>Mesh topology</h2>
        <span style={{ fontSize: 14, lineHeight: '20px', color: T.body }}>
          {weak.length
            ? weak.length + (weak.length === 1 ? ' node' : ' nodes') + ' below ' + WEAK_QUALITY + '% link quality or off the mesh — likely weak points'
            : 'All links above ' + WEAK_QUALITY + '% quality'}
        </span>
      </div>
      <section style={{ ...panel, marginBottom: 24 }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 560, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {loading && <div style={{ fontSize: 14, color: T.muted }}>Loading…</div>}
            {tiers.filter((tier) => tier.nodes.length > 0).map((tier, index) => (
              <div key={tier.key} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {index > 0 && <div style={{ width: 1, height: 20, background: T.hairline }} />}
                {tier.key === 'Infinity' &&
                  <div style={{ ...eyebrow, marginBottom: 8 }}>Hop count not reported</div>}
                <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
                  {tier.nodes.map((n) => {
                    const gw = n.isGateway();
                    const focused = focus && focus.getNodeAddress() === n.getNodeAddress();
                    const off = n.getIsConnected() === false;
                    return (
                      <button
                        type='button'
                        key={n.getNodeAddress()}
                        className={classes.meshNode}
                        title={describe(n)}
                        onClick={() => setFocusAddress(gw ? null : n.getNodeAddress())}
                        style={{
                          border: '1px solid ' + (gw ? (off ? T.hairline : T.ink) : (focused ? T.ink : T.hairline)),
                          background: gw ? (off ? T.surfaceCard : T.ink) : (focused ? T.surfaceCard : T.canvas)
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Dot
                            size={9}
                            color={gw
                              ? (off ? INDICATOR.error : INDICATOR.success)
                              : (off ? INDICATOR.error : qualityFill(n.getNextHopQuality()))}
                          />
                          <span style={{ ...mono, fontSize: 12, fontWeight: 500, color: gw && !off ? T.canvas : T.ink }}>
                            {n.getNodeAddress()}
                          </span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, ...mono, fontSize: 12, color: gw && !off ? T.hairlineSoft : T.mutedSoft }}>
                          <span>{n.getRoleLabel()}</span>
                          <span>{formatQuality(n)}</span>
                          {!gw && <span>{formatRssi(n)}</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px', marginTop: 24, paddingTop: 24, borderTop: '1px solid ' + T.hairlineSoft }}>
          {QUALITY_LEGEND.map((l) => (
            <span key={l.label} style={{ ...eyebrow, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Dot color={l.color} />{l.label}
            </span>
          ))}
        </div>
      </section>

      {/* Route to gateway */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
        <h2 style={sectionTitle}>Route to gateway</h2>
        <span style={{ fontSize: 14, lineHeight: '20px', color: T.body }}>
          {focus
            ? focus.getNodeAddress() + ' · ' + (focus.getHopCount() == null ? 'hops not reported' : focus.getHopCount() + (focus.getHopCount() === 1 ? ' hop' : ' hops') + ' to gateway')
            : 'No nodes on this network'}
        </span>
      </div>
      <section style={{ ...panel, marginBottom: 48 }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', width: 'max-content' }}>
            {route.length === 0 &&
              <div style={{ fontSize: 14, color: T.muted }}>{loading ? 'Loading…' : 'Select a node to trace its route.'}</div>}
            {route.map((n, i) => {
              const gw = n.isGateway();
              const unknown = n.getKind() === NetworkNode.KINDS.UNKNOWN && !byAddress.has(n.getNodeAddress());
              const next = route[i + 1];
              const facts = gw
                ? [['Role', 'Gateway'], ['Status', statusOf(n).label], ['Last seen', formatSeen(n)]]
                : unknown
                  ? [['Role', '—'], ['Status', 'Not on the node list']]
                  : [['Role', n.getRoleLabel()], ['RSSI', formatRssi(n)], ['Battery', formatBattery(n)]];
              return (
                <div key={n.getNodeAddress() + ':' + i} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: 220, border: '1px solid ' + (gw ? T.ink : T.hairline), borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: gw ? T.ink : T.surfaceCard }}>
                      <Dot
                        size={9}
                        color={gw
                          ? (n.getIsConnected() === false ? INDICATOR.error : INDICATOR.success)
                          : qualityFill(n.getIsConnected() === false ? 0 : n.getNextHopQuality())}
                      />
                      <span style={{ ...mono, fontSize: 12, fontWeight: 500, color: gw ? T.canvas : T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {n.getNodeAddress()}
                      </span>
                    </div>
                    {facts.map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 12px', borderTop: '1px solid ' + T.hairlineSoft }}>
                        <span style={eyebrow}>{k}</span>
                        <span style={{ ...mono, fontSize: 12, color: T.ink }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  {next &&
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 96 }}>
                      <span style={{ ...mono, fontSize: 12, fontWeight: 500, color: qualityText(n.getNextHopQuality()) }}>
                        {formatQuality(n)}
                      </span>
                      <span
                        style={{
                          width: '100%',
                          height: 2,
                          background: qualityFill(n.getNextHopQuality()),
                          opacity: n.getIsConnected() === false ? 0.4 : 1
                        }}
                      />
                    </div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Node diagnostics */}
      <section style={{ background: T.elevated, border: '1px solid ' + T.hairline, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: 24, borderBottom: '1px solid ' + T.hairlineSoft }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, lineHeight: '28px', letterSpacing: '-0.4px', color: T.ink }}>
            Node diagnostics
          </h2>
          <span style={{ background: T.hairlineSoft, borderRadius: 9999, padding: '4px 12px', fontSize: 12, fontWeight: 500, lineHeight: '16px', color: T.ink }}>
            {rows.length + (rows.length === 1 ? ' node' : ' nodes')}
          </span>
          <div style={{ flex: '1 0 12px' }} />
          <PillTrack>
            {TABLE_TABS.map((t) => (
              <Pill key={t.key} on={tab === t.key} onClick={() => setTab(t.key)}>{t.label}</Pill>
            ))}
          </PillTrack>
          <button type='button' className={classes.button} onClick={() => exportCsv('latest')} disabled={busy != null}>
            {busy === 'latest' ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            type='button'
            className={classes.button}
            onClick={() => exportCsv('history')}
            disabled={busy != null}
            title='Every report received in the last 24 hours'
          >
            {busy === 'history' ? 'Exporting…' : 'Export 24h history'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 1100 }}>
            <div style={{ display: 'grid', gridTemplateColumns: TABLE_GRID, gap: 16, padding: '12px 24px', background: T.surfaceSoft, borderBottom: '1px solid ' + T.hairline }}>
              {TABLE_COLUMNS.map((label) => <span key={label} style={eyebrow}>{label}</span>)}
            </div>
            {rows.length === 0 &&
              <div style={{ padding: 24, fontSize: 14, color: T.muted }}>{loading ? 'Loading…' : 'No nodes of this kind on the network.'}</div>}
            {rows.map((n) => {
              const status = statusOf(n);
              const focused = focus && focus.getNodeAddress() === n.getNodeAddress();
              const quality = n.isGateway() ? (n.getIsConnected() === false ? null : 100) : n.getNextHopQuality();
              return (
                <div
                  key={n.getNodeAddress()}
                  className={classes.row}
                  style={focused ? { background: T.surfaceCard } : undefined}
                  onClick={() => setFocusAddress(n.isGateway() ? null : n.getNodeAddress())}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...mono, fontSize: 14, fontWeight: 500, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.getNodeAddress()}
                    </div>
                    <div style={{ ...eyebrow, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {describe(n)}
                    </div>
                  </div>
                  <span style={{ fontSize: 14, color: T.body }}>{n.getRoleLabel()}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: status.text, whiteSpace: 'nowrap' }}>
                    <Dot color={status.dot} size={7} />{status.label}
                  </span>
                  <span style={{ ...mono, fontSize: 14, color: latencyText(n) }}>{formatLatency(n)}</span>
                  <span style={{ ...mono, fontSize: 14, color: T.body }}>{formatSeen(n)}</span>
                  <span style={{ ...mono, fontSize: 14, color: batteryText(n) }}>{formatBattery(n)}</span>
                  <span style={{ ...mono, fontSize: 14, color: T.body }}>{formatRssi(n)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...mono, fontSize: 12, color: qualityText(quality) }}>{formatQuality(n)}</div>
                    <div style={{ height: 4, borderRadius: 9999, marginTop: 4, background: T.hairlineSoft, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: (quality ?? 0) + '%', background: qualityFill(quality) }} />
                    </div>
                  </div>
                  <span style={{ ...mono, fontSize: 14, color: T.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {n.getNextHop() ?? '—'}
                  </span>
                  <span style={{ ...mono, fontSize: 14, color: T.body }}>{n.getHopCount() ?? '—'}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

NetworkPerformance.propTypes = {
  userType: PropTypes.object,
  setError: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  userType: state.auth.userType
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg)
});

export default connect(mapStateToProps, mapDispatchToProps)(NetworkPerformance);
